import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, getDocs, getDoc, doc,
  query, where, orderBy, limit, startAfter,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Emogiface, Emogiface2, Emogiface3, Emogiface4, Emogiface5 } from '../../../tools/MyIcons';
import ExcelJS from 'exceljs';

import {
  ThemeProvider, createTheme, CssBaseline,
  Box, Typography, Card, CardContent, Chip,
  LinearProgress, Stack, CircularProgress,
  Alert, Fade, Grow, Button,
  Avatar, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';

import PeopleAltIcon           from '@mui/icons-material/PeopleAlt';
import ListAltIcon             from '@mui/icons-material/ListAlt';
import AccessTimeIcon          from '@mui/icons-material/AccessTime';
import WbSunnyRoundedIcon      from '@mui/icons-material/WbSunnyRounded';
import DarkModeRoundedIcon     from '@mui/icons-material/DarkModeRounded';
import ChevronRightIcon        from '@mui/icons-material/ChevronRight';
import ArrowBackIcon           from '@mui/icons-material/ArrowBack';
import NavigateNextIcon        from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon      from '@mui/icons-material/NavigateBefore';
import CalendarMonthIcon       from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon  from '@mui/icons-material/CheckCircleOutline';
import StarRoundedIcon         from '@mui/icons-material/StarRounded';
import DownloadIcon            from '@mui/icons-material/Download';

// ─── i18n ─────────────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  en: {
    filterAll: 'All',
    pageTitle:        'Response Detail',
    statResponses:    'Total responses',
    statPage:         'Page',
    backToList:       'Back to list',
    detailTitle:      'Response detail',
    noData:           'No responses yet for this survey.',
    loading:          'Loading…',
    loadingResponses: 'Loading responses...',
    errorSurvey:      'Survey not found.',
    errorGeneric:     'Could not load responses.',
    prevPage:         'Previous',
    nextPage:         'Next',
    respondent:       'Respondent',
    period:           'Period',
    question:         'Question',
    toggleLight:      'Switch to light mode',
    toggleDark:       'Switch to dark mode',
    typeRating:       'Rating',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Multiple choice',
    typeSlider:       'Range',
    typeText:         'Free text',
    additionalComment: 'Additional Comment',
    correctAnswer:    'Correct',
    yourAnswer:       'Your answer',
    colIndex:         '#',
    colId:            'ID',
    colPeriod:        'Period',
    colPregunta:      'Question',
    colRespuesta:     'Answer',
    colDetail:        'Detail',
    viewDetail:       'View detail',
    noAnswer:         'No answer',
    exportExcel:      'Export to Excel',
    exporting:        'Exporting...',
    months: ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  },
  es: {
    filterAll: 'Todo',
    pageTitle:        'Detalle de Respuestas',
    statResponses:    'Total respuestas',
    statPage:         'Página',
    backToList:       'Volver al listado',
    detailTitle:      'Detalle de respuesta',
    noData:           'Aún no hay respuestas para este survey.',
    loading:          'Cargando…',
    loadingResponses: 'Cargando respuestas...',
    errorSurvey:      'Survey no encontrado.',
    errorGeneric:     'No se pudieron cargar las respuestas.',
    prevPage:         'Anterior',
    nextPage:         'Siguiente',
    respondent:       'Respondiente',
    period:           'Período',
    question:         'Pregunta',
    toggleLight:      'Cambiar a modo claro',
    toggleDark:       'Cambiar a modo oscuro',
    typeRating:       'Calificación',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Múltiple',
    typeSlider:       'Rango',
    typeText:         'Texto libre',
    additionalComment: 'Comentario adicional',
    correctAnswer:    'Correcta',
    yourAnswer:       'Tu respuesta',
    colIndex:         '#',
    colId:            'ID',
    colPeriod:        'Período',
    colPregunta:      'Pregunta',
    colRespuesta:     'Respuesta',
    colDetail:        'Detalle',
    viewDetail:       'Ver detalle',
    noAnswer:         'Sin respuesta',
    exportExcel:      'Exportar a Excel',
    exporting:        'Exportando...',
    months: ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
  },
};

const detectLang = () => {
  const lang = (navigator?.language || navigator?.languages?.[0] || 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
};

const useTranslations = () => {
  const lang = useMemo(() => detectLang(), []);
  return TRANSLATIONS[lang] ?? TRANSLATIONS.en;
};

// ─── Palette ──────────────────────────────────────────────────────────────────
const getPalette = (mode) => {
  const isDark = mode === 'dark';
  return {
    isDark,
    pageBg: isDark
      ? ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.13) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.09) 0%, transparent 50%)',
         '#0D0F1A'].join(', ')
      : ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.07) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.05) 0%, transparent 50%)',
         '#F4F5FF'].join(', '),
    cardBg: (accent)   => isDark ? `linear-gradient(160deg, ${accent}14 0%, #151828 55%)` : `linear-gradient(160deg, ${accent}0C 0%, #FFFFFF 60%)`,
    statCardBg: (c)    => isDark ? `linear-gradient(135deg, ${c}22 0%, #15182800 70%)` : `linear-gradient(135deg, ${c}14 0%, #FFFFFF00 70%)`,
    statCardBorder:(c) => isDark ? `1px solid ${c}38 !important` : `1px solid ${c}50 !important`,
    commentText:       isDark ? '#EEF0FF' : '#1A1C2E',
    timeColor:         isDark ? '#4A4F6A' : '#9CA3AF',
    rowBg:             isDark ? 'rgba(255,255,255,0.03)' : 'rgba(108,99,255,0.03)',
    rowHoverBg:        isDark ? 'rgba(108,99,255,0.09)'  : 'rgba(108,99,255,0.06)',
    rowBorder:         isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(108,99,255,0.09)',
    miscChipBg:        isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    headerLogoBg:      'linear-gradient(135deg, #6C63FF, #FF6584)',
    toggleBg:          isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    toggleHover:       isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
    toggleColor:       isDark ? '#F9C74F' : '#6C63FF',
    tableHeaderBg:     isDark ? 'rgba(108,99,255,0.10)' : 'rgba(108,99,255,0.06)',
  };
};

const buildTheme = (mode) => createTheme({
  palette: {
    mode,
    primary:   { main: '#6C63FF' },
    secondary: { main: '#FF6584' },
    success:   { main: '#43E97B' },
    warning:   { main: '#F9C74F' },
    error:     { main: '#FF6B6B' },
    background: mode === 'dark'
      ? { default: '#0D0F1A', paper: '#151828' }
      : { default: '#F4F5FF', paper: '#FFFFFF' },
    text: mode === 'dark'
      ? { primary: '#EEF0FF', secondary: '#8B90B8' }
      : { primary: '#1A1C2E', secondary: '#6B7280' },
  },
  typography: {
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    h5:        { fontWeight: 800, letterSpacing: '-0.02em' },
    subtitle2: { fontWeight: 600 },
    caption:   { letterSpacing: '0.06em' },
  },
  shape: { borderRadius: 16 },
});

// ─── Constants & Helpers ──────────────────────────────────────────────────────
const RATING_GRADIENT = ['#FF6B6B','#FF9F43','#F9C74F','#A8E063','#43E97B'];
const PAGE_SIZE = 5;

// ─── Emoji sets ───────────────────────────────────────────────────────────────
const EMOJI_SETS = {
  emojis:        Emogiface,
  emojis2:       Emogiface2,
  emojisCat:     Emogiface3,
  emojispeople:  Emogiface4,
  emojisnumbers: Emogiface5,
};

const periodKeyFromData = (data) => `${data.anio ?? ''}_${String(data.month ?? '').padStart(2, '0')}`;
const periodLabel = (key, months) => {
  const [year, month] = key.split('_');
  return `${months[parseInt(month, 10)]} ${year}`;
};
const currentPeriodKey = () => {
  const now = new Date();
  return `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatDateWithSeconds = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const lang = detectLang();
  return d.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const lang = detectLang();
  return d.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const uidInitials = (uid = '') => uid.slice(-2).toUpperCase();
const stringColor = (str = '') => {
  const palette = ['#6C63FF','#FF6584','#43E97B','#F9C74F','#38BDF8','#FB923C','#A78BFA'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

const getSortedQuestions = (questionsMap) => {
  if (!questionsMap) return [];
  return Object.entries(questionsMap)
    .map(([key, q]) => ({ key, ...q }))
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
};

const getTypeLabel = (type) => ({
  rating:        '#6C63FF',
  emojis:        '#FF6584',
  emojis2:       '#FF6584',
  emojisCat:     '#FF6584',
  emojispeople:  '#FF6584',
  emojisnumbers: '#FF6584',
  radio:         '#43E97B',
  slider:        '#38BDF8',
  text:          '#8B90B8',
}[type] || '#888');

// Trunca texto largo para que quepa en una celda de la tabla.
const truncateText = (str = '', max = 60) => (
  str.length > max ? `${str.slice(0, Math.max(0, max - 1))}…` : str
);

// Resuelve la respuesta seleccionada a texto plano (sin imágenes), para la
// columna "Respuesta" de la tabla principal.
const answerToDisplayText = (type, userAnswer, question, t) => {
  if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
    return t?.noAnswer ?? '—';
  }
  if (type === 'radio') {
    const options = question?.options || [];
    const userIdx = parseInt(userAnswer, 10);
    const opt = options.find((o, idx) => parseInt(o.id ?? idx + 1, 10) === userIdx);
    return truncateText(opt?.text || `Opción ${userIdx}`, 60);
  }
  switch (type) {
    case 'rating':
      return `${parseInt(userAnswer, 10)} / 5`;
    case 'emojis':
    case 'emojis2':
    case 'emojisCat':
    case 'emojispeople':
    case 'emojisnumbers':
      return `${parseInt(userAnswer, 10)}`;
    case 'slider':
      return `${userAnswer}`;
    case 'text':
      return truncateText(String(userAnswer), 70);
    default:
      return truncateText(String(userAnswer), 60);
  }
};

// Convierte la respuesta seleccionada a un valor entero plano, para la
// columna "Respuesta" del Excel exportado (sin texto descriptivo tipo
// "4 / 5" o "#3"). Para texto libre no hay un entero real, así que se deja
// el string tal cual llegó.
const answerToNumericValue = (type, userAnswer) => {
  if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
    return null;
  }
  switch (type) {
    case 'rating':
    case 'radio':
    case 'emojis':
    case 'emojis2':
    case 'emojisCat':
    case 'emojispeople':
    case 'emojisnumbers':
      return parseInt(userAnswer, 10);
    case 'slider':
      return parseFloat(userAnswer);
    case 'text':
      return String(userAnswer);
    default: {
      const n = parseInt(userAnswer, 10);
      return Number.isNaN(n) ? String(userAnswer) : n;
    }
  }
};

// ─── Answer Renderers (usados solo en el detalle) ─────────────────────────────
const RatingAnswer = ({ val }) => {
  const v = parseInt(val, 10);
  const color = RATING_GRADIENT[v - 1] || '#888';
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Stack direction="row" spacing={0.3}>
        {Array.from({ length: 5 }, (_, i) => (
          <StarRoundedIcon key={i} sx={{ fontSize: 20, color: i < v ? color : 'rgba(128,128,128,0.2)' }} />
        ))}
      </Stack>
      <Typography variant="body1" sx={{ fontWeight: 700, color }}>{v} / 5</Typography>
    </Stack>
  );
};

const EmojiAnswer = ({ val, type }) => {
  const v = parseInt(val, 10);
  const faceSet = EMOJI_SETS[type] || {};
  const faceKeys = Object.keys(faceSet)
    .filter(k => /^face\d+$/.test(k))
    .sort((a, b) => parseInt(a.replace('face', ''), 10) - parseInt(b.replace('face', ''), 10));

  return (
    <Stack direction="row" flexWrap="wrap" gap={1.5}>
      {faceKeys.map((k) => {
        const faceNum = parseInt(k.replace('face', ''), 10);
        const isSelected = faceNum === v;
        return (
          <Box
            key={k}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              p: 1.5,
              borderRadius: 3,
              border: `2px solid ${isSelected ? '#FF6584' : '#e0e0e0'}`,
              background: isSelected ? 'rgba(255,101,132,0.08)' : 'transparent',
              minWidth: 72,
            }}
          >
            <img src={faceSet[k]} alt={k} style={{ width: 42, height: 42 }} />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isSelected && <CheckCircleOutlineIcon sx={{ color: '#FF6584', fontSize: 16 }} />}
              <Typography variant="caption" sx={{ fontWeight: isSelected ? 700 : 400 }}>
                {faceNum}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const SliderAnswer = ({ val, sliderConfig }) => {
  const v = parseFloat(val);
  const min = sliderConfig?.min ?? 1;
  const max = sliderConfig?.max ?? 5;
  const pct = ((v - min) / (max - min)) * 100;
  const color = RATING_GRADIENT[Math.round(((v - min) / (max - min)) * 4)] || '#6C63FF';
  return (
    <Stack direction="row" alignItems="center" spacing={3}>
      <Box sx={{ px: 2, py: 0.8, borderRadius: 3, background: 'rgba(108,99,255,0.12)', border: '1px solid rgba(108,99,255,0.4)' }}>
        <Typography variant="h6" fontWeight={700}>{v}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, maxWidth: 220, borderRadius: 99, height: 8 }} />
    </Stack>
  );
};

const TextAnswer = ({ val }) => (
  <Typography sx={{
    fontStyle: 'italic',
    background: 'rgba(108,99,255,0.06)',
    p: 2.5,
    borderRadius: 3,
    border: '1px solid rgba(108,99,255,0.15)',
    maxWidth: '100%',
  }}>
    {val || '—'}
  </Typography>
);

const renderAnswerWithCorrect = (type, userAnswer, question, t) => {
  if (type === 'radio') {
    const options = question?.options || [];
    const userIdx = parseInt(userAnswer, 10);
    const correctIdx = parseInt(question?.correctOption || question?.correctAnswer || -1, 10);
    return (
      <Stack spacing={1.5} direction="row" flexWrap="wrap" gap={1.5}>
        {options.map((opt, idx) => {
          const optId = parseInt(opt.id ?? idx + 1, 10);
          const isUser = optId === userIdx;
          const isCorrect = optId === correctIdx;
          return (
            <Box
              key={optId}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `2px solid ${isCorrect ? '#43E97B' : isUser ? '#FF6584' : '#e0e0e0'}`,
                background: isCorrect ? 'rgba(67,233,123,0.08)' : isUser ? 'rgba(255,101,132,0.08)' : 'transparent',
                minWidth: 180,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                {isCorrect && <CheckCircleOutlineIcon sx={{ color: '#43E97B' }} />}
                <Typography variant="body2" fontWeight={isCorrect || isUser ? 600 : 400}>
                  {opt.text || `Opción ${optId}`}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  }
  switch (type) {
    case 'rating':
      return <RatingAnswer val={userAnswer} />;
    case 'emojis':
    case 'emojis2':
    case 'emojisCat':
    case 'emojispeople':
    case 'emojisnumbers':
      return <EmojiAnswer val={userAnswer} type={type} />;
    case 'slider':
      return <SliderAnswer val={userAnswer} sliderConfig={question?.sliderConfig || question?.config} />;
    case 'text':
      return <TextAnswer val={userAnswer} />;
    default:
      return <Typography variant="body1">{String(userAnswer || '—')}</Typography>;
  }
};

// ─── Response Detail ──────────────────────────────────────────────────────────
const ResponseDetail = ({ responseDoc, surveyQuestions, t, p, onBack }) => {
  const { answer = {}, createdBy = '', addRec, anio, month, comm } = responseDoc;
  const sortedQuestions = useMemo(() => getSortedQuestions(surveyQuestions), [surveyQuestions]);
  const color = stringColor(createdBy);
  const hasComment = !!comm && comm.trim() !== '';

  return (
    <Fade in timeout={400}>
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          mb={4}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
          >
            {t.backToList}
          </Button>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              avatar={
                <Avatar sx={{ background: `${color}33`, color, fontSize: '0.65rem', fontWeight: 700 }}>
                  {uidInitials(createdBy)}
                </Avatar>
              }
              label={`${t.respondent}: ${createdBy ? createdBy.slice(-6) : '—'}`}
              size="small"
              sx={{ background: p.miscChipBg }}
            />
            <Chip
              icon={<CalendarMonthIcon sx={{ fontSize: 13 }} />}
              label={`${t.period}: ${t.months[month] ?? month} ${anio}`}
              size="small"
              sx={{ background: p.miscChipBg }}
            />
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
              label={formatDate(addRec)}
              size="small"
              sx={{ background: p.miscChipBg }}
            />
          </Stack>
        </Stack>

        <Stack spacing={3.5}>
          {sortedQuestions.map((q, idx) => {
            const ansEntry = Object.values(answer).find(a => a.orden === q.orden);
            const userAnswer = ansEntry?.ans ?? null;
            const accent = getTypeLabel(q.type);
            const imageUrl = q.url || '';

            return (
              <Grow key={q.key} in timeout={400} style={{ transitionDelay: `${idx * 60}ms` }}>
                <Card sx={{
                  background: p.cardBg(accent),
                  borderTop: `5px solid ${accent}`,
                  overflow: 'hidden',
                }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>

                    {/* Número + texto de la pregunta */}
                    <Box sx={{ mb: imageUrl ? 2 : 3 }}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: p.commentText, display: 'flex', alignItems: 'flex-start', gap: 2 }}
                      >
                        <Box sx={{
                          minWidth: 32, height: 32, borderRadius: '50%',
                          background: `${accent}22`, color: accent,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1rem', fontWeight: 800, flexShrink: 0,
                        }}>
                          {idx + 1}
                        </Box>
                        {q.text}
                      </Typography>
                    </Box>

                    {/* Imagen de la pregunta */}
                    {imageUrl && (
                      <Box
                        sx={{
                          mb: 3,
                          ml: { xs: 0, sm: 6 },
                          borderRadius: 2,
                          overflow: 'hidden',
                          lineHeight: 0,
                          border: `1px solid ${accent}22`,
                          maxWidth: 480,
                        }}
                      >
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={q.text}
                          sx={{
                            width: '100%',
                            maxHeight: { xs: 180, sm: 240 },
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'scale(1.02)' },
                          }}
                        />
                      </Box>
                    )}

                    {/* Respuesta del usuario */}
                    <Box sx={{
                      pl: { xs: 0, sm: 6 },
                      borderLeft: { xs: 'none', sm: `3px solid ${accent}35` },
                    }}>
                      <Typography
                        variant="caption"
                        sx={{
                          mb: 1.5, display: 'block', fontWeight: 600,
                          letterSpacing: '0.8px', color: 'text.secondary',
                          textTransform: 'uppercase',
                        }}
                      >
                        {t.yourAnswer}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {userAnswer !== null
                          ? renderAnswerWithCorrect(q.type, userAnswer, q, t)
                          : <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>{t.noAnswer}</Typography>
                        }
                      </Box>
                    </Box>

                  </CardContent>
                </Card>
              </Grow>
            );
          })}
        </Stack>

        {hasComment && (
          <Card sx={{ mt: 4, background: p.cardBg('#8B90B8'), borderTop: '4px solid #8B90B8' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#8B90B8', fontWeight: 600 }}>
                📝 {t.additionalComment}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {comm}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Fade>
  );
};

// Convierte la lista de respuestas de la página en filas "planas": una fila
// por cada pregunta contestada dentro de cada respuesta. Las columnas # /
// Período / flecha se agrupan con rowSpan para no repetirse en cada pregunta.
const buildFlatRows = (docs, surveyQuestions, startIndex) => {
  const sortedQuestions = getSortedQuestions(surveyQuestions);
  const rows = [];
  docs.forEach((responseDoc, docPos) => {
    const questions = sortedQuestions.length > 0 ? sortedQuestions : [null];
    questions.forEach((q, qIdx) => {
      rows.push({
        rowKey: `${responseDoc.id}_${q?.key ?? 'na'}`,
        responseDoc,
        question: q,
        index: startIndex + docPos,
        groupIndex: docPos,
        isFirstOfGroup: qIdx === 0,
        groupSize: questions.length,
        isNewGroup: docPos > 0 && qIdx === 0,
      });
    });
  });
  return rows;
};

// ─── Response Table Row (tabla principal, 1 fila por pregunta) ───────────────
const ResponseTableRow = ({ row, delay, t, p, onSelect }) => {
  const { responseDoc, question: q, index, groupIndex, isFirstOfGroup, groupSize, isNewGroup } = row;
  const { addRec, anio, month, answer = {} } = responseDoc;
  const isOddGroup = groupIndex % 2 === 1;

  const ansEntry = q ? Object.values(answer).find(a => a.orden === q.orden) : null;
  const userAnswer = ansEntry?.ans ?? null;

  return (
    <Grow in timeout={400} style={{ transitionDelay: `${delay}ms` }}>
      <TableRow
        hover
        sx={{
          background: isOddGroup ? p.rowBg : 'transparent',
          '&:last-child td, &:last-child th': { border: 0 },
          '&:hover': { background: p.rowHoverBg },
          ...(isNewGroup ? { '& td': { borderTop: p.rowBorder } } : {}),
        }}
      >
        {isFirstOfGroup && (
          <TableCell rowSpan={groupSize} align="center" sx={{ width: 40, verticalAlign: 'middle' }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '8px', mx: 'auto',
              background: 'rgba(108,99,255,0.12)', border: '1px solid rgba(108,99,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#6C63FF' }}>
                {index}
              </Typography>
            </Box>
          </TableCell>
        )}

        {isFirstOfGroup && (
          <TableCell rowSpan={groupSize} align="center" sx={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: p.commentText, textAlign: 'center' }}>
              {formatDateWithSeconds(addRec)}
            </Typography>
            <Typography variant="caption" sx={{ color: p.timeColor, display: 'block', textAlign: 'center' }}>
              {t.months[month] ?? month} {anio}
            </Typography>
          </TableCell>
        )}

        <TableCell sx={{ minWidth: 200 }}>
          <Typography variant="body2" sx={{ color: p.commentText }}>
            {q ? truncateText(q.text || '—', 60) : '—'}
          </Typography>
        </TableCell>

        <TableCell sx={{ minWidth: 160 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: p.commentText }}>
            {q ? answerToDisplayText(q.type, userAnswer, q, t) : '—'}
          </Typography>
        </TableCell>

        {isFirstOfGroup && (
          <TableCell rowSpan={groupSize} align="center" sx={{ width: 140, verticalAlign: 'middle' }}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              onClick={() => onSelect(responseDoc)}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {t.viewDetail}
            </Button>
          </TableCell>
        )}
      </TableRow>
    </Grow>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, hasNext, hasPrev, onPrev, onNext, t }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mt={4}>
    <Button startIcon={<NavigateBeforeIcon />} onClick={onPrev} disabled={!hasPrev} variant="outlined" size="small" sx={{ borderRadius: 3, textTransform: 'none' }}>
      {t.prevPage}
    </Button>
    <Box sx={{ px: 3, py: 1, borderRadius: 3, background: 'rgba(108,99,255,0.1)', border: '1px solid rgba(108,99,255,0.3)' }}>
      <Typography variant="body2" fontWeight={700} color="#6C63FF">
        {t.statPage} {page}
      </Typography>
    </Box>
    <Button endIcon={<NavigateNextIcon />} onClick={onNext} disabled={!hasNext} variant="outlined" size="small" sx={{ borderRadius: 3, textTransform: 'none' }}>
      {t.nextPage}
    </Button>
  </Stack>
);

// ─── FilterBar ────────────────────────────────────────────────────────────────
const FilterBar = ({ periods, selected, onChange, t }) => {
  const items = periods.length > 1
    ? [{ key: 'all', label: t.filterAll }, ...periods.map(pk => ({ key: pk, label: periodLabel(pk, t.months) }))]
    : periods.map(pk => ({ key: pk, label: periodLabel(pk, t.months) }));

  return (
    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2, mb: 3 }}>
      {items.map(item => (
        <Chip
          key={item.key}
          label={item.label}
          onClick={() => onChange(item.key)}
          variant={selected === item.key ? 'filled' : 'outlined'}
          color={selected === item.key ? 'primary' : 'default'}
          sx={{ fontWeight: selected === item.key ? 700 : 500 }}
        />
      ))}
    </Box>
  );
};

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, p }) => (
  <Card sx={{ flex: 1, background: p.statCardBg(color), border: p.statCardBorder(color) }}>
    <CardContent sx={{ p: '10px 14px !important', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
        background: `${color}1F`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, lineHeight: 1 }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color, lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '0.62rem', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// ─── ThemeToggle ──────────────────────────────────────────────────────────────
const ThemeToggle = ({ mode, onToggle, t, p }) => (
  <Tooltip title={mode === 'light' ? t.toggleDark : t.toggleLight}>
    <IconButton onClick={onToggle} sx={{ background: p.toggleBg, color: p.toggleColor, '&:hover': { background: p.toggleHover } }}>
      {mode === 'light' ? <DarkModeRoundedIcon /> : <WbSunnyRoundedIcon />}
    </IconButton>
  </Tooltip>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ResultadoDetalle = ({ surveyId }) => {
  const t = useTranslations();

  const [themeMode, setThemeMode] = useState('light');
  const toggleMode = useCallback(() => setThemeMode(m => m === 'light' ? 'dark' : 'light'), []);
  const theme = useMemo(() => buildTheme(themeMode), [themeMode]);
  const p = useMemo(() => getPalette(themeMode), [themeMode]);

  const [surveyMeta, setSurveyMeta]           = useState({ title: '', description: '' });
  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [allPeriods, setAllPeriods]           = useState([]);
  const [selected, setSelected]               = useState('all');
  const [page, setPage]                       = useState(1);
  const [cursors, setCursors]                 = useState([null]);
  const [docs, setDocs]                       = useState([]);
  const [hasNext, setHasNext]                 = useState(false);
  const [selectedDoc, setSelectedDoc]         = useState(null);
  const [totalCount, setTotalCount]           = useState(0);
  const [loading, setLoading]                 = useState(true);
  const [loadingPage, setLoadingPage]         = useState(false);
  const [error, setError]                     = useState('');
  const [exporting, setExporting]             = useState(false);

  useEffect(() => {
    if (!surveyId) return;
    const init = async () => {
      try {
        const surveySnap = await getDoc(doc(db, 'surveys', surveyId));
        if (!surveySnap.exists()) { setError(t.errorSurvey); setLoading(false); return; }

        const data = surveySnap.data();
        setSurveyQuestions(data.questions || {});
        setSurveyMeta({ title: data.title ?? '', description: data.description ?? '' });

        const answersRef = collection(db, 'surveys', surveyId, 'answers');
        const allSnap = await getDocs(answersRef);

        const periodSet = new Set();
        let count = 0;
        allSnap.forEach((d) => {
          const ad = d.data();
          if (ad.anio && ad.month !== undefined) periodSet.add(periodKeyFromData(ad));
          count++;
        });

        setTotalCount(count);
        const sorted = [...periodSet].sort((a, b) => b.localeCompare(a));
        setAllPeriods(sorted);

        const cur = currentPeriodKey();
        let initial = 'all';
        if (periodSet.has(cur)) initial = cur;
        else if (sorted.length > 0) initial = sorted[0];
        setSelected(initial);
      } catch (err) {
        console.error(err);
        setError(t.errorGeneric);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [surveyId, t]);

  const fetchPage = useCallback(async (cursorDoc) => {
    setLoadingPage(true);
    try {
      const answersRef = collection(db, 'surveys', surveyId, 'answers');
      const constraints = [];

      if (selected !== 'all') {
        const [anio, month] = selected.split('_');
        constraints.push(where('anio', '==', parseInt(anio, 10)));
        constraints.push(where('month', '==', parseInt(month, 10)));
      }

      constraints.push(orderBy('addRec', 'desc'));
      constraints.push(limit(PAGE_SIZE + 1));
      if (cursorDoc) constraints.push(startAfter(cursorDoc));

      const snap = await getDocs(query(answersRef, ...constraints));
      const fetched = snap.docs;
      const hasNextPage = fetched.length > PAGE_SIZE;
      const pageItems = hasNextPage ? fetched.slice(0, PAGE_SIZE) : fetched;

      setDocs(pageItems.map(d => ({ id: d.id, _snap: d, ...d.data() })));
      setHasNext(hasNextPage);

      if (hasNextPage) {
        setCursors(prev => {
          const updated = [...prev];
          updated[page] = pageItems[pageItems.length - 1]._snap ?? pageItems[pageItems.length - 1];
          return updated;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPage(false);
    }
  }, [surveyId, selected, page]);

  useEffect(() => {
    setPage(1);
    setCursors([null]);
    setSelectedDoc(null);
    if (surveyId) fetchPage(null);
  }, [selected, surveyId, fetchPage]);

  const handleNext = () => {
    const nextPage = page + 1;
    const cursorSnap = cursors[page];
    setPage(nextPage);
    fetchPage(cursorSnap);
  };

  const handlePrev = () => {
    if (page <= 1) return;
    const prevPage = page - 1;
    setPage(prevPage);
    fetchPage(cursors[prevPage - 1] || null);
  };

  const handlePeriodChange = (key) => {
    setSelected(key);
    setSelectedDoc(null);
  };

  // ─── Exportar tabla principal a Excel (ExcelJS) ───────────────────────────
  // Usa exactamente los datos ya renderizados en la tabla HTML (docs de la
  // página actual), con las mismas filas planas que arma buildFlatRows,
  // y genera un .xlsx con encabezado con estilo, autofiltro y fila congelada.
  //
  // Columnas: # | ID (documento) | Pregunta | Respuesta (entero) | Fecha
  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const startIndex = (page - 1) * PAGE_SIZE + 1;
      const rows = buildFlatRows(docs, surveyQuestions, startIndex);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'EmojiSurveys';
      workbook.created = new Date();

      const sheetName = (surveyMeta.title || t.pageTitle).slice(0, 31) || 'Respuestas';
      const sheet = workbook.addWorksheet(sheetName);

      sheet.columns = [
        { header: t.colIndex,     key: 'index',      width: 8  },
        { header: t.colId,        key: 'id',         width: 26 },
        { header: t.colPregunta,  key: 'pregunta',    width: 45 },
        { header: t.colRespuesta, key: 'respuesta',   width: 14 },
        { header: t.period,       key: 'fecha',       width: 22 },
      ];

      // Encabezado con estilo
      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6C63FF' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      rows.forEach((row) => {
        const { responseDoc, question: q, index } = row;
        const { addRec, answer = {} } = responseDoc;
        const ansEntry = q ? Object.values(answer).find(a => a.orden === q.orden) : null;
        const userAnswer = ansEntry?.ans ?? null;

        sheet.addRow({
          index,
          id: responseDoc.id.slice(0, 12),
          pregunta: q ? (q.text || '—') : '—',
          respuesta: q ? answerToNumericValue(q.type, userAnswer) : null,
          fecha: formatDateWithSeconds(addRec),
        });
      });

      // Bordes y wrap para todas las celdas con datos
      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top:    { style: 'thin', color: { argb: 'FFE0E0E0' } },
            left:   { style: 'thin', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            right:  { style: 'thin', color: { argb: 'FFE0E0E0' } },
          };
          cell.alignment = { vertical: 'middle', wrapText: true };
        });
      });

      sheet.autoFilter = { from: 'A1', to: 'E1' };
      sheet.views = [{ state: 'frozen', ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const safeTitle = (surveyMeta.title || 'respuestas').replace(/[^a-z0-9_-]+/gi, '_');
      const fileName = `${safeTitle}_${selected}.xlsx`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  }, [docs, page, selected, surveyQuestions, surveyMeta, t]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');`}</style>

      <Box sx={{ minHeight: '100vh', background: p.pageBg, px: { xs: 1.5, sm: 3, md: 4 }, py: { xs: 2.5, sm: 4 } }}>

        <Fade in timeout={400}>
          <Box mb={4}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ flex: 1 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: p.headerLogoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(108,99,255,0.4)' }}>
                  <ListAltIcon sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
                <Box sx={{ width: '90%' }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', width: '100%', color: p.commentText }}>
                    {surveyMeta.title || t.pageTitle}
                  </Typography>
                  {surveyMeta.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, width: '100%', textAlign: 'center' }}>
                      {surveyMeta.description}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <ThemeToggle mode={themeMode} onToggle={toggleMode} t={t} p={p} />
            </Stack>
          </Box>
        </Fade>

        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
            <CircularProgress size={50} sx={{ color: '#6C63FF' }} />
            <Typography color="text.secondary">{t.loading}</Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}

        {!loading && !error && allPeriods.length === 0 && (
          <Box textAlign="center" py={10}>
            <Typography sx={{ fontSize: '4rem' }}>📭</Typography>
            <Typography color="text.secondary" mt={2}>{t.noData}</Typography>
          </Box>
        )}

        {!loading && !error && allPeriods.length > 0 && (
          <>
            {!selectedDoc && (
              <>
                <FilterBar periods={allPeriods} selected={selected} onChange={handlePeriodChange} t={t} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={4} alignItems="stretch">
                  <StatCard icon={<PeopleAltIcon sx={{ fontSize: 17 }} />} label={t.statResponses} value={totalCount} color="#6C63FF" p={p} />
                  <StatCard icon={<ListAltIcon sx={{ fontSize: 17 }} />}   label={t.statPage}      value={page}       color="#43E97B" p={p} />
                  <Button
                    onClick={handleExportExcel}
                    disabled={exporting || totalCount === 0}
                    variant="contained"
                    disableElevation
                    startIcon={exporting ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <DownloadIcon sx={{ fontSize: 17 }} />}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      whiteSpace: 'nowrap',
                      px: 2.25,
                      background: 'linear-gradient(135deg, #6C63FF, #7C6CFF)',
                      boxShadow: '0 2px 10px rgba(108,99,255,0.32)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5A52E0, #6C63FF)',
                        boxShadow: '0 4px 14px rgba(108,99,255,0.42)',
                      },
                      '&.Mui-disabled': {
                        background: p.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        color: p.isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    {exporting ? t.exporting : t.exportExcel}
                  </Button>
                </Stack>
              </>
            )}

            {!selectedDoc && (
              <>
                {loadingPage && (
                  <Box display="flex" justifyContent="center" py={8} flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress size={36} sx={{ color: '#6C63FF' }} />
                    <Typography color="text.secondary">{t.loadingResponses}</Typography>
                  </Box>
                )}

                {!loadingPage && docs.length > 0 && (
                  <TableContainer sx={{ borderRadius: '12px', border: p.rowBorder, overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: p.tableHeaderBg }}>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{t.colIndex}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{t.colPeriod}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t.colPregunta}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t.colRespuesta}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{t.colDetail}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {buildFlatRows(docs, surveyQuestions, (page - 1) * PAGE_SIZE + 1).map((row, i) => (
                          <ResponseTableRow
                            key={row.rowKey}
                            row={row}
                            delay={i * 30}
                            t={t}
                            p={p}
                            onSelect={setSelectedDoc}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!loadingPage && docs.length > 0 && (
                  <Pagination page={page} hasNext={hasNext} hasPrev={page > 1} onNext={handleNext} onPrev={handlePrev} t={t} p={p} />
                )}
              </>
            )}

            {selectedDoc && (
              <ResponseDetail
                responseDoc={selectedDoc}
                surveyQuestions={surveyQuestions}
                t={t}
                p={p}
                onBack={() => setSelectedDoc(null)}
              />
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ResultadoDetalle;